import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { GalleriesFilterResponse, GalleryVisibility, GalleryVisibilityFilter, OrderDirection } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { GalleryService } from '../../Services/GalleryService';
import { EntityColors } from '../../Themes/EntityColors';
import CustomTooltip from '../Tooltips/CustomTooltip';
import EditButton from '../Buttons/EditButton';
import DeleteButton from '../Buttons/DeleteButton';
import { Utils } from '../../Services/Utils';
import GalleryDeleteModal from '../Modals/GalleryDeleteModal';
import CropPreviewComponent from '../Image/CropPreview';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';
import UserAvatarCell from '../Avatar/UserAvatarCell';

const GalleryTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Galérie');
    }, []);

    const { filterGalleries, deleteGallery } = GalleryService();
    const [response, setResponse] = useState<GalleriesFilterResponse | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(6);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const [visibilityFilter, setVisibilityFilter] = useState<GalleryVisibilityFilter>('All');
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.gallery;

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [toDeleteId, setToDeleteId] = useState<number | null>(null);

    const toVisibilityArray = (filter: GalleryVisibilityFilter): GalleryVisibility[] =>
        filter === 'All' ? ['Public', 'Hidden'] : [filter];

    const loadPage = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection; visibility?: GalleryVisibilityFilter }) => {
        const limit = opts?.perPage ?? perPage;
        const resp = await filterGalleries({
            websiteId: currentSite.id,
            limit,
            offset: (pageNum - 1) * limit,
            visibility: toVisibilityArray(opts?.visibility ?? visibilityFilter),
            orderDirection: opts?.order ?? orderDirection,
        });
        setResponse(resp ?? null);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void loadPage(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void loadPage(1, { order: dir });
    };

    const changeVisibilityFilter = (visibility: GalleryVisibilityFilter) => {
        setVisibilityFilter(visibility);
        setPage(1);
        void loadPage(1, { visibility });
    };

    const confirmDelete = () => {
        if (toDeleteId !== null) {
            deleteGallery(toDeleteId).then(() => {
                setShowDeleteModal(false);
                setToDeleteId(null);
                loadPage(1);
                setPage(1);
            });
        }
    };

    useEffect(() => {
        setPage(1);
        void loadPage(1);
    }, [currentSite]);

    return (
        <>
            <TableShell
                title="Galérie"
                icon={icon}
                accentColor={action}
                createHref="/gallery/create"
                isLoading={response === null}
                isEmpty={response !== null && response.data.length === 0}
                emptyMessage="Žiadne galérie"
                page={page}
                totalCount={response?.totalCount}
                perPage={perPage}
                onPageChange={p => { setPage(p); loadPage(p); }}
                toolbar={
                    <TableToolbar
                        totalCount={response?.totalCount ?? 0}
                        nounForms={['galéria', 'galérie', 'galérií']}
                        perPage={perPage}
                        onPerPageChange={changePerPage}
                        orderDirection={orderDirection}
                        onOrderChange={changeOrder}
                        filter={{
                            label: 'Viditeľnosť',
                            value: visibilityFilter,
                            onChange: value => changeVisibilityFilter(value as GalleryVisibilityFilter),
                            options: [
                                { value: 'All', label: 'Všetky' },
                                { value: 'Public', label: 'Verejné' },
                                { value: 'Hidden', label: 'Skryté' },
                            ],
                        }}
                    />
                }
            >
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Nadpis</th>
                            <th>Titulný obrázok</th>
                            <th>Počet obrázkov</th>
                            <th>Viditeľnosť</th>
                            <th>Vytvorené</th>
                            <th>Vytvoril</th>
                            <th>Naposledy upravené</th>
                            <th>Upravil</th>
                            <th className="text-center">Akcie</th>
                        </tr>
                    </thead>
                    <tbody>
                        {response?.data.map(gallery => (
                            <tr key={gallery.id}>
                                <td>{gallery.title}</td>
                                <td>
                                    {gallery.titleImage?.filePath
                                        ? <CropPreviewComponent filePath={gallery.titleImage.filePath} alt={gallery.titleImage.alt} />
                                        : <span className="st-cell-dash">–</span>}
                                </td>
                                <td>{gallery.galleryImages?.length ?? 0}</td>
                                <td>
                                    <span className={`st-pill ${gallery.visibility === 'Hidden' ? 'st-pill--hidden' : 'st-pill--visible'}`}>
                                        {gallery.visibility === 'Hidden' ? 'Skrytá' : 'Verejná'}
                                    </span>
                                </td>
                                <td className="text-nowrap">
                                    {Utils.formatDate(gallery.createdAt)}
                                </td>
                                <td>
                                    <UserAvatarCell userId={gallery.createdById} />
                                </td>
                                <td className="text-nowrap st-cell-soft">
                                    {gallery.modifiedAt ? Utils.formatDate(gallery.modifiedAt) : '—'}
                                </td>
                                <td>
                                    <UserAvatarCell userId={gallery.modifiedById} />
                                </td>
                                <td className="text-center">
                                    <div className="st-row-actions">
                                        <CustomTooltip placement="left" message="Upraviť">
                                            <Link to={`/gallery/edit/${gallery.id}`}>
                                                <EditButton color={action} iconOnly />
                                            </Link>
                                        </CustomTooltip>
                                        <CustomTooltip placement="right" message="Zmazať">
                                            <DeleteButton
                                                color={action}
                                                iconOnly
                                                onClick={() => { setToDeleteId(gallery.id); setShowDeleteModal(true); }}
                                            />
                                        </CustomTooltip>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableShell>

            {showDeleteModal && (
                <GalleryDeleteModal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    onSubmit={confirmDelete}
                    idToDelete={toDeleteId}
                />
            )}
        </>
    );
};

export default GalleryTableComponent;
